/** P-11 / P-12 — patient order detail: address, items, totals, status, cancel, pay. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import { describeError } from "../../api/errors";
import { canPatientCancel } from "../../lib/orders";
import { formatINR } from "../../lib/money";
import { PAUSED_REFRESH_MS, failureReasonText, formatCountdown, usePauseCountdown } from "../../lib/orderPayment";
import { cancelMyOrder, getMyOrder, requestOrderCancel } from "../../api/orders";
import { isPaid, payForOrder } from "../../payments/checkout";
import type { PaymentStage } from "../../payments/checkout";
import { OrderStatusBadge } from "../../components/OrderStatusBadge";


export function OrderDetail() {
  const { id = "" } = useParams();
  const qc = useQueryClient();
  const { data: order, isLoading, isError, refetch } = useQuery({
    queryKey: ["my-order", id],
    queryFn: () => getMyOrder(id),
    // While the pharmacy revises the price, check for the new one (ORD-22).
    refetchInterval: (query) => (query.state.data?.price_revision ? PAUSED_REFRESH_MS : false),
  });
  const pausedFor = usePauseCountdown(order?.price_revision);
  const paused = !!order?.price_revision && pausedFor !== 0;
  useEffect(() => {
    if (pausedFor === 0) void refetch(); // the edit window ended: the old price can be paid again
  }, [pausedFor, refetch]);

  const [payNotice, setPayNotice] = useState<{ tone: "error" | "info"; text: string } | null>(null);
  // Asking to cancel a paid order and be refunded (ORD-16).
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestReason, setRequestReason] = useState("");
  const requestCancel = useMutation({
    mutationFn: () => requestOrderCancel(id, requestReason.trim()),
    onSuccess: (o) => {
      qc.setQueryData(["my-order", id], o);
      setRequestOpen(false);
      setRequestReason("");
    },
    onError: () => refetchOrder(),
  });
  const [payStage, setPayStage] = useState<PaymentStage | null>(null);
  const refetchOrder = () => qc.invalidateQueries({ queryKey: ["my-order", id] });

  // A failed cancel says why and shows the order as it now is (ORD-12): it may have moved on.
  const cancel = useMutation({
    mutationFn: () => cancelMyOrder(id, "cancelled by patient"),
    onSuccess: refetchOrder,
    onError: (e) => {
      toast.error(describeError(e));
      refetchOrder();
    },
  });

  // payForOrder never throws: it reports the outcome (ORD-01).
  const pay = useMutation({
    mutationFn: () => payForOrder(id, { onStage: setPayStage }),
    onSuccess: (result) => {
      refetchOrder();
      if (isPaid(result) || result.status === "cancelled") setPayNotice(null);
      else setPayNotice({ tone: result.status === "pending" ? "info" : "error", text: result.message });
    },
    onSettled: () => setPayStage(null),
  });
  const payLabel =
    payStage === "checkout"
      ? "Complete the payment in the Razorpay window…"
      : payStage === "verifying"
        ? "Confirming your payment…"
        : "Starting payment…";

  if (isLoading) return <p className="text-sm text-gray-400">Loading…</p>;
  if (isError || !order) return <p className="text-sm text-red-600">Order not found.</p>;

  const addr = order.delivery_address_snapshot as Record<string, string>;
  const priced = order.pricing_published_at !== null;

  return (
    <div className="max-w-2xl space-y-5">
      <Link to="/patient/orders" className="text-sm text-brand hover:underline">
        ← Back to orders
      </Link>

      <div className="rounded-xl border bg-white p-5 shadow-xs">
        <div className="flex items-center justify-between">
          <div>
            {/* The reference a patient quotes to support, so it leads the card. */}
            <div className="font-mono text-base font-semibold">{order.order_id}</div>
            <div className="mt-1">
              <OrderStatusBadge code={order.status_code} label={order.status_label} />
            </div>
          </div>
          {canPatientCancel(order.status_code) && (
            <button
              onClick={() => {
                // A mis-click no longer cancels at once (ORD-12).
                if (!cancel.isPending && window.confirm("Cancel this order? You can order this prescription again while it is still valid.")) cancel.mutate();
              }}
              disabled={cancel.isPending}
              className="rounded-lg border px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              {cancel.isPending ? "Cancelling…" : "Cancel order"}
            </button>
          )}
        </div>
        {order.placement_note && <p className="mt-3 rounded-lg bg-gray-50 p-3 text-sm">{order.placement_note}</p>}
        <div className="mt-3 text-sm text-gray-600">
          <div className="text-gray-400">Delivering to</div>
          <div>{addr.name} · {addr.phone}</div>
          <div>{addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}, {addr.city}, {addr.state_code} {addr.pin_code}</div>
        </div>
      </div>

      <div className="rounded-xl border bg-white shadow-xs">
        <div className="border-b px-5 py-3 text-sm font-medium text-gray-500">Items</div>
        <ul className="divide-y">
          {order.lines.map((ln) => (
            // A long name wraps on its own side; the price keeps its place and one line (RESP-12).
            <li key={ln.id} className="flex items-start justify-between gap-3 px-5 py-3 text-sm">
              <div className="min-w-0">
                <div className="font-medium wrap-anywhere">{ln.medicine_name}</div>
                <div className="text-xs text-gray-400">
                  {ln.is_available ? `Qty ${ln.quantity_dispensed || ln.quantity_prescribed}` : "Unavailable"}
                  {ln.quantity_remark ? ` · ${ln.quantity_remark}` : ""}
                </div>
              </div>
              {priced && ln.is_available && <span className="shrink-0 whitespace-nowrap">{formatINR(ln.taxable_value)}</span>}
            </li>
          ))}
        </ul>
        {priced ? (
          <div className="space-y-1 border-t px-5 py-3 text-sm">
            <Row label="Medicines" value={`₹${order.medicines_subtotal}`} />
            <Row label="Delivery" value={`₹${order.delivery_charge}`} />
            <Row label="GST" value={`₹${order.gst_total}`} />
            <div className="flex justify-between pt-1 font-semibold">
              <span>Total</span>
              <span>₹{order.final_amount}</span>
            </div>
          </div>
        ) : (
          <div className="border-t px-5 py-3 text-sm text-gray-500">
            Waiting for pricing by our team — we’ll let you know as soon as your order is priced.
          </div>
        )}
      </div>

      {order.status_code === "awaiting_payment" && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          {paused ? (
            <p role="status" data-testid="payment-paused" className="text-sm text-amber-800">
              <span className="font-medium">The pharmacy is updating the price.</span> You can pay once the new price
              is ready{pausedFor !== null ? ` — at the latest in ${formatCountdown(pausedFor)}` : ""}. This page updates
              on its own.
            </p>
          ) : (
            <p className="text-sm text-amber-800">Your order is priced and ready to pay.</p>
          )}
          {!paused && !payNotice && order.payment?.status === "failed" && (
            <p className="mt-2 text-sm text-red-600">
              {failureReasonText(order.payment)} No money was taken for it — you can safely try again.
            </p>
          )}
          {payNotice && (
            <p className={`mt-2 text-sm ${payNotice.tone === "error" ? "text-red-600" : "text-amber-800"}`}>
              {payNotice.text}
            </p>
          )}
          <button
            onClick={() => { setPayNotice(null); pay.mutate(); }}
            disabled={pay.isPending || paused}
            className="mt-3 rounded-lg bg-brand px-5 py-2 text-sm font-semibold text-brand-fg disabled:opacity-50"
          >
            {pay.isPending
              ? payLabel
              : paused
                ? "Payment paused"
                : `${order.payment?.status === "failed" ? "Retry payment" : "Pay"} ${formatINR(order.final_amount)}`}
          </button>
        </div>
      )}

      {order.status_code === "paid" && order.cancel_request?.status !== "open" && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
          Payment received — ₹{order.final_amount} paid. We’ll start preparing your order.
        </div>
      )}

      {/* Cancel & refund request (ORD-16): under review, or declined with the team's note. */}
      {order.cancel_request?.status === "open" && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <div className="font-medium">Cancellation requested</div>
          Our team is reviewing your request; your order is on hold meanwhile.
          {order.refund_estimate && ` If it is cancelled, we’ll refund ${formatINR(order.refund_estimate)}.`}
        </div>
      )}
      {order.cancel_request?.status === "declined" && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <div className="font-medium">Cancellation request declined</div>
          {order.cancel_request.decline_note ? `${order.cancel_request.decline_note} ` : ""}Your order goes ahead.
        </div>
      )}

      {order.can_request_cancel && !requestOpen && (
        <button
          onClick={() => { requestCancel.reset(); setRequestOpen(true); }}
          className="rounded-lg border border-red-200 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
        >
          Request cancellation &amp; refund
        </button>
      )}
      {order.can_request_cancel && requestOpen && (
        <form
          onSubmit={(e) => { e.preventDefault(); requestCancel.mutate(); }}
          className="space-y-3 rounded-xl border bg-white p-4 shadow-xs"
        >
          <div className="font-medium">Cancel and get a refund?</div>
          <p className="text-sm text-gray-600">
            Our team reviews your request before the order is dispatched, and holds the order meanwhile.
            {order.refund_estimate &&
              ` If it is cancelled, we’ll refund ${formatINR(order.refund_estimate)} — the amount paid less GST and transaction fees.`}
          </p>
          <label className="flex flex-col gap-1 text-sm">
            <span className="text-gray-500">Why do you want to cancel?</span>
            <textarea
              value={requestReason}
              onChange={(e) => setRequestReason(e.target.value)}
              required
              minLength={3}
              maxLength={1000}
              rows={3}
              className="rounded-lg border px-3 py-2"
            />
          </label>
          {requestCancel.error && <p className="text-sm text-red-600">{describeError(requestCancel.error)}</p>}
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <button type="button" onClick={() => setRequestOpen(false)} className="rounded-lg border px-4 py-2 text-sm">
              Keep my order
            </button>
            <button
              disabled={requestCancel.isPending || requestReason.trim().length < 3}
              className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {requestCancel.isPending ? "Sending…" : "Send request"}
            </button>
          </div>
        </form>
      )}

      {order.status_code === "refund_initiated" && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          {order.refund ? `A refund of ${formatINR(order.refund.amount)} has been initiated.` : "A refund has been initiated."} It
          usually reaches your original payment method within 5–7 working days.
        </div>
      )}

      {order.status_code === "refunded" && (
        <div className="rounded-xl border bg-gray-50 p-4 text-sm text-gray-600">
          {order.refund
            ? `${formatINR(order.refund.amount)} was refunded to your original payment method${
                order.refund.processed_at ? ` on ${new Date(order.refund.processed_at).toLocaleDateString()}` : ""
              }.`
            : "This order was refunded."}{" "}
          To order again, you’ll need a fresh prescription.
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between text-gray-600">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
